import React, { useState, useRef } from 'react';
import { Box, Button, TextField, Paper, Typography, CircularProgress, Alert } from '@mui/material';
import { CloudUpload, Delete, Image } from '@mui/icons-material';
import { uploadData, getUrl } from 'aws-amplify/storage';

const ImageUpload = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const fileName = `news-images/${Date.now()}-${file.name}`;
      const result = await uploadData({
        path: `public/${fileName}`,
        data: file,
        options: {
          contentType: file.type
        }
      }).result;
      
      // Store just the key, not the full URL
      onChange(fileName);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (event) => {
    onChange(event.target.value);
    setError(null);
  };

  const handleRemove = () => {
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box>
      <Box display="flex" gap={2} mb={2}>
        <Button
          variant="outlined"
          startIcon={<CloudUpload />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Upload Image'}
        </Button>
        {value && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleRemove}
          >
            Remove
          </Button>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <TextField
        fullWidth
        label="Or enter image URL"
        value={value}
        onChange={handleUrlChange}
        placeholder="https://example.com/image.jpg"
        disabled={uploading}
      />

      {uploading && (
        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <CircularProgress size={16} />
          <Typography variant="caption">Uploading image...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {value && !uploading && (
        <Paper variant="outlined" sx={{ mt: 2, p: 1 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Image fontSize="small" />
            <Typography variant="caption" sx={{ color: 'success.main' }}>
              ✓ Image uploaded: {value.split('/').pop()}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Key: {value}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ImageUpload;
